import { useEffect, useRef, useState } from 'react';
import { getIngestionConfig, getIngestionStatus, runAllTargetAccounts, triggerIngestionRun } from '../api/client';
import { timeAgo, preciseTimeAgo, formatDate } from '../familyMeta';
import { ChevronDown, ChevronRight, Check } from 'lucide-react';

const POLL_MS = 4000;
const DAYS_BOUNDS = { min: 1, max: 365 };

// Discovery-mode phases run strictly in this order (see ingestion_runner.py's
// _run_all) — a phase only gets a key in status.sources once it finishes, so
// the first not-yet-present phase is always the one currently running.
const DISCOVERY_PHASES = [
  { key: 'hiring', label: 'Hiring' },
  { key: 'funding', label: 'Funding' },
  { key: 'research', label: 'Research' },
];

function summarizePhaseResult(result) {
  if (result.error) return `failed — ${result.error}`;
  const parts = [`${result.persisted || 0} new signal${result.persisted === 1 ? '' : 's'}`];
  if (result.errors) parts.push(`${result.errors} persist errors`);
  return parts.join(', ');
}

/** 'done' | 'failed' | 'current' | 'queued' — phases run in DISCOVERY_PHASES
 * order and each only appears in `sources` once finished, so the first
 * phase without a result yet is the one currently running. */
function phaseStatus(sources, key) {
  const result = sources?.[key];
  if (result) return result.error ? 'failed' : 'done';
  const index = DISCOVERY_PHASES.findIndex((p) => p.key === key);
  const doneCount = DISCOVERY_PHASES.filter((p) => sources?.[p.key]).length;
  return index === doneCount ? 'current' : 'queued';
}

export function summarizeSources(sources) {
  const parts = [];
  let totalPersisted = 0;
  let totalErrors = 0;
  for (const [name, result] of Object.entries(sources || {})) {
    if (result.error) {
      parts.push(`${name} failed (${result.error})`);
      continue;
    }
    totalPersisted += result.persisted || 0;
    totalErrors += result.errors || 0;
    parts.push(`${result.persisted || 0} ${name}`);
  }
  const summary = parts.length ? `${totalPersisted} new signals — ${parts.join(', ')}` : 'no new signals';
  return totalErrors > 0 ? `${summary} (${totalErrors} persist errors)` : summary;
}

export default function IngestionControl({ latestObservedAt, onRunComplete, showWatchlistRun }) {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [availableSites, setAvailableSites] = useState([]);
  const [selectedSites, setSelectedSites] = useState(null); // null until config loads
  const [availableFundingSources, setAvailableFundingSources] = useState([]);
  const [selectedFundingSources, setSelectedFundingSources] = useState(null); // null until config loads
  const [availableResearchSources, setAvailableResearchSources] = useState([]);
  const [selectedResearchSources, setSelectedResearchSources] = useState(null); // null until config loads
  const [days, setDays] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    getIngestionConfig()
      .then((cfg) => {
        setAvailableSites(cfg.available_sites);
        setSelectedSites(new Set(cfg.default_sites));
        setAvailableFundingSources(cfg.available_funding_sources || []);
        setSelectedFundingSources(new Set(cfg.default_funding_sources || []));
        setAvailableResearchSources(cfg.available_research_sources || []);
        setSelectedResearchSources(new Set(cfg.default_research_sources || []));
        setDays(cfg.default_days);
      })
      .catch(() => {
        // backend unreachable — options panel just won't populate; Run
        // ingestion still works, it'll use the backend's own defaults.
      });
  }, []);

  function stopPolling() {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }

  function poll() {
    getIngestionStatus()
      .then((s) => {
        setError(null);
        setStatus(s);
        if (s.status !== 'running') {
          stopPolling();
          if (s.status === 'completed') onRunComplete?.();
        }
      })
      .catch((e) => {
        // A transient status request must not freeze the UI in "Running".
        // Keep polling so the next successful response can finish the run.
        setError(`Status check failed; retrying automatically. ${e.message}`);
      });
  }

  useEffect(() => {
    poll(); // pick up an in-progress run on page load
    return stopPolling;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function toggleInSet(setter) {
    return (value) => {
      setter((prev) => {
        const next = new Set(prev);
        if (next.has(value)) next.delete(value);
        else next.add(value);
        return next;
      });
    };
  }

  const toggleSite = toggleInSet(setSelectedSites);
  const toggleFundingSource = toggleInSet(setSelectedFundingSources);
  const toggleResearchSource = toggleInSet(setSelectedResearchSources);

  function handleRun() {
    setError(null);
    triggerIngestionRun({
      sites: selectedSites ? [...selectedSites] : undefined,
      funding_sources: selectedFundingSources ? [...selectedFundingSources] : undefined,
      research_sources: selectedResearchSources ? [...selectedResearchSources] : undefined,
      days: days || undefined,
    })
      .then((s) => {
        setStatus(s);
        stopPolling();
        pollRef.current = setInterval(poll, POLL_MS);
      })
      .catch((e) => setError(e.message));
  }

  function handleRunWatchlist() {
    setError(null);
    runAllTargetAccounts({
      research_sources: selectedResearchSources ? [...selectedResearchSources] : undefined,
      days: days || undefined,
    })
      .then((s) => {
        setStatus(s);
        stopPolling();
        pollRef.current = setInterval(poll, POLL_MS);
      })
      .catch((e) => setError(e.message));
  }

  const running = status?.status === 'running';
  const targeted = status?.mode === 'targeted';
  const runButtonLabel =
    running && !targeted
      ? 'Running…'
      : status?.status === 'completed'
        ? 'Run again'
        : status?.status === 'failed'
          ? 'Retry'
          : 'Run ingestion';

  return (
    <div className="ingestion-control-wrap">
      <div className="ingestion-control">
        <div className="ingestion-meta">
          <span className="lbl">Data last updated</span>
          <span className="val">{latestObservedAt ? `${formatDate(latestObservedAt)} · ${timeAgo(latestObservedAt)}` : 'never'}</span>
        </div>
        <button
          type="button"
          className="advanced-toggle"
          onClick={() => setOptionsOpen((v) => !v)}
          disabled={running}
        >
          {optionsOpen ? <ChevronDown size={15} aria-hidden="true" /> : <ChevronRight size={15} aria-hidden="true" />} Ingestion options
        </button>
        <button className="btn primary" onClick={handleRun} disabled={running}>
          {running && !targeted && <span className="btn-spinner" aria-hidden="true" />}
          {runButtonLabel}
        </button>
        {showWatchlistRun && (
          <button
            type="button"
            className="btn"
            onClick={handleRunWatchlist}
            disabled={running}
            title="Checks every active watchlist account for new funding and research activity, using the options above"
          >
            {running && targeted && <span className="btn-spinner" aria-hidden="true" />}
            {running && targeted ? 'Running watchlist…' : 'Run watchlist'}
          </button>
        )}
      </div>

      {optionsOpen && (
        <div className="ingestion-options">
          <div className="ingestion-options-block">
            <div className="rail-title">HIRING SOURCES</div>
            {availableSites.length === 0 && <div className="hint">Loading…</div>}
            {availableSites.map((site) => (
              <div className="src-row" key={site}>
                <label>
                  <input
                    type="checkbox"
                    checked={selectedSites?.has(site) ?? true}
                    onChange={() => toggleSite(site)}
                    disabled={running}
                  />
                  {site}
                </label>
              </div>
            ))}
          </div>
          <div className="ingestion-options-block">
            <div className="rail-title">FUNDING SOURCES</div>
            {availableFundingSources.length === 0 && <div className="hint">Loading…</div>}
            {availableFundingSources.map((source) => (
              <div className="src-row" key={source}>
                <label>
                  <input
                    type="checkbox"
                    checked={selectedFundingSources?.has(source) ?? true}
                    onChange={() => toggleFundingSource(source)}
                    disabled={running}
                  />
                  {source}
                </label>
              </div>
            ))}
          </div>
          <div className="ingestion-options-block">
            <div className="rail-title">RESEARCH / RELEASE SOURCES</div>
            {availableResearchSources.length === 0 && <div className="hint">Loading…</div>}
            {availableResearchSources.map((source) => (
              <div className="src-row" key={source}>
                <label>
                  <input
                    type="checkbox"
                    checked={selectedResearchSources?.has(source) ?? true}
                    onChange={() => toggleResearchSource(source)}
                    disabled={running}
                  />
                  {source}
                </label>
              </div>
            ))}
          </div>
          <div className="ingestion-options-block">
            <div className="rail-title">LOOKBACK WINDOW</div>
            <label className="days-input">
              <input
                type="number"
                min={DAYS_BOUNDS.min}
                max={DAYS_BOUNDS.max}
                value={days ?? ''}
                onChange={(e) => setDays(Number(e.target.value))}
                disabled={running}
              />
              days back (applies to hiring, funding &amp; research)
            </label>
          </div>
        </div>
      )}

      {error && <div className="ingestion-status error">{error}</div>}
      {!error && status?.status === 'running' && targeted && (
        <div className="ingestion-status">
          <span className="btn-spinner" aria-hidden="true" /> Checking active watchlist accounts for new funding and research activity…
        </div>
      )}
      {!error && status?.status === 'running' && !targeted && (
        <div className="ingestion-status">
          <div className="ingestion-hint">
            Scraping hiring, funding &amp; research sources — this can take several minutes (hiring is the slow one).
          </div>
          <ul className="ingestion-phase-list">
            {DISCOVERY_PHASES.map((phase) => {
              const result = status.sources?.[phase.key];
              const phaseState = phaseStatus(status.sources, phase.key);
              return (
                <li key={phase.key} className={`ingestion-phase ingestion-phase-${phaseState}`}>
                  <span className="ingestion-phase-icon" aria-hidden="true">
                    {phaseState === 'current' ? <span className="btn-spinner" /> : phaseState === 'done' ? <Check size={13} aria-hidden="true" /> : phaseState === 'failed' ? '✕' : '·'}
                  </span>
                  {phase.label}
                  {result && <span className="ingestion-phase-detail"> — {summarizePhaseResult(result)}</span>}
                </li>
              );
            })}
          </ul>
        </div>
      )}
      {!error && status?.status === 'completed' && (
        <div className="ingestion-status">
          {targeted ? 'Last watchlist check' : 'Last run'} finished {preciseTimeAgo(status.finished_at)}: {summarizeSources(status.sources)}
        </div>
      )}
      {!error && status?.status === 'failed' && (
        <div className="ingestion-status error">The ingestion run failed. Review the error and retry.</div>
      )}
    </div>
  );
}
